import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Building2, SkipForward } from "lucide-react";
import { saveOrgProfileMinimal, skipOrgProfileWizard } from "../../services/orgProfileService";
import { useOrgProfile } from "../../Moduels/Context/OrgProfileContext";
import { useSetupProgress } from "../../Moduels/Context/SetupProgressContext";
import BrixigoPageLoader from "../common/BrixigoPageLoader";
import "../../styles/orgProfileWizard.css";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry",
];

const PAGE_TITLE = "Organisation Profile";

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Organisation name is required";
  const pan = form.pan.trim().toUpperCase();
  if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) errors.pan = "Invalid PAN (e.g. ABCDE1234F)";
  const tan = form.tan_number.trim().toUpperCase();
  if (tan && tan.length < 10) errors.tan_number = "TAN must be at least 10 characters";
  if (!form.state.trim()) errors.state = "State is required";
  if (!form.address.trim()) errors.address = "Address is required";
  return errors;
}

export default function OrgProfileWizard() {
  const navigate = useNavigate();
  const { status, refetch, mustCompleteWizard } = useOrgProfile();
  const setupCtx = useSetupProgress();
  const [form, setForm] = useState({
    name: "",
    legal_name: "",
    pan: "",
    tan_number: "",
    state: "",
    address: "",
    country: "India",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [apiError, setApiError] = useState("");

  const isFirstVisit = mustCompleteWizard;

  useEffect(() => {
    const o = status.org || {};
    setForm((prev) => ({
      ...prev,
      name: o.name || prev.name,
      legal_name: o.legal_name || prev.legal_name,
      pan: o.pan || prev.pan,
      tan_number: o.tan_number || prev.tan_number,
      state: o.state || prev.state,
      address: o.address || prev.address,
      country: o.country || "India",
    }));
  }, [status.org]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setApiError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    setApiError("");
    try {
      await saveOrgProfileMinimal({
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || undefined,
        pan: form.pan.trim().toUpperCase() || undefined,
        tan_number: form.tan_number.trim().toUpperCase() || undefined,
        state: form.state.trim(),
        address: form.address.trim(),
        country: form.country || "India",
      });
      await refetch();
      setupCtx?.refetch?.();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setApiError(typeof detail === "string" ? detail : "Could not save profile. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const onSkip = async () => {
    setSkipping(true);
    setApiError("");
    try {
      await skipOrgProfileWizard();
      await refetch();
      setupCtx?.refetch?.();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setApiError(typeof detail === "string" ? detail : "Could not skip right now. Try again.");
      setShowSkipConfirm(false);
    } finally {
      setSkipping(false);
    }
  };

  if (status.loading) {
    return <BrixigoPageLoader message="Loading…" />;
  }

  return (
    <div className="op-wizard-page">
      <div className="op-wizard-card">
        <div className="op-wizard-hero">
          <div className="op-wizard-hero-badge">
            <Building2 size={14} aria-hidden />
            <span>{PAGE_TITLE}</span>
          </div>
          <h1>Welcome — set up your organisation</h1>
          <p>
            Add your company details for payroll, Form 16, and statutory compliance.
            {isFirstVisit
              ? " You can complete this now or skip and finish later from the dashboard."
              : " Update your registered company information below."}
          </p>
        </div>

        {isFirstVisit && (
          <div className="op-wizard-warning" role="status">
            <AlertTriangle size={18} className="op-wizard-warning-icon" aria-hidden />
            <div>
              <strong>Recommended before payroll</strong>
              <p>
                Incomplete <em>{PAGE_TITLE}</em> may block payslips, TDS, and compliance filings.
                A reminder will appear at the top of your dashboard until this is done.
              </p>
            </div>
          </div>
        )}

        {!isFirstVisit && !status.profileComplete && (
          <div className="op-wizard-warning op-wizard-warning--edit" role="status">
            <AlertTriangle size={18} className="op-wizard-warning-icon" aria-hidden />
            <div>
              <strong>{PAGE_TITLE} incomplete</strong>
              <p>Fill in the missing fields below to enable payroll and compliance features.</p>
            </div>
          </div>
        )}

        <form className="op-wizard-body" onSubmit={onSubmit} noValidate>
          {apiError && <div className="op-banner-err">{apiError}</div>}

          <div className="op-field">
            <label className="op-label" htmlFor="name">
              Organisation Name <span className="op-req">*</span>
            </label>
            <input
              id="name"
              name="name"
              className={`op-input${errors.name ? " err" : ""}`}
              value={form.name}
              onChange={onChange}
              placeholder="Acme Technologies Pvt Ltd"
            />
            {errors.name && <span className="op-err">{errors.name}</span>}
          </div>

          <div className="op-field">
            <label className="op-label" htmlFor="legal_name">
              Legal Name <span className="op-label-opt">(optional)</span>
            </label>
            <input
              id="legal_name"
              name="legal_name"
              className="op-input"
              value={form.legal_name}
              onChange={onChange}
              placeholder="Same as registered legal entity"
            />
          </div>

          <div className="op-field-row">
            <div className="op-field">
              <label className="op-label" htmlFor="pan">
                PAN <span className="op-label-opt">(optional)</span>
              </label>
              <input
                id="pan"
                name="pan"
                className={`op-input${errors.pan ? " err" : ""}`}
                value={form.pan}
                onChange={onChange}
                placeholder="ABCDE1234F"
                maxLength={10}
                style={{ textTransform: "uppercase" }}
              />
              {errors.pan && <span className="op-err">{errors.pan}</span>}
            </div>

            <div className="op-field">
              <label className="op-label" htmlFor="tan_number">
                TAN <span className="op-label-opt">(optional)</span>
              </label>
              <input
                id="tan_number"
                name="tan_number"
                className={`op-input${errors.tan_number ? " err" : ""}`}
                value={form.tan_number}
                onChange={onChange}
                placeholder="HYDA12345A"
                style={{ textTransform: "uppercase" }}
              />
              {errors.tan_number && <span className="op-err">{errors.tan_number}</span>}
            </div>
          </div>

          <div className="op-field">
            <label className="op-label" htmlFor="state">
              State <span className="op-req">*</span>
            </label>
            <select
              id="state"
              name="state"
              className={`op-select${errors.state ? " err" : ""}`}
              value={form.state}
              onChange={onChange}
            >
              <option value="">Select state</option>
              {INDIAN_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {errors.state && <span className="op-err">{errors.state}</span>}
          </div>

          <div className="op-field">
            <label className="op-label" htmlFor="address">
              Address <span className="op-req">*</span>
            </label>
            <textarea
              id="address"
              name="address"
              className={`op-textarea${errors.address ? " err" : ""}`}
              value={form.address}
              onChange={onChange}
              placeholder="Registered office address"
            />
            {errors.address && <span className="op-err">{errors.address}</span>}
          </div>

          {showSkipConfirm ? (
            <div className="op-skip-confirm" role="alertdialog" aria-labelledby="op-skip-title">
              <p id="op-skip-title" className="op-skip-confirm-title">Skip {PAGE_TITLE} for now?</p>
              <p className="op-skip-confirm-text">
                You can enter the app, but payroll and compliance will stay limited until you complete
                <strong> {PAGE_TITLE}</strong> from the dashboard reminder.
              </p>
              <div className="op-skip-confirm-actions">
                <button
                  type="button"
                  className="op-btn-cancel"
                  onClick={() => setShowSkipConfirm(false)}
                  disabled={skipping}
                >
                  Go back
                </button>
                <button
                  type="button"
                  className="op-btn-skip-confirm"
                  onClick={onSkip}
                  disabled={skipping}
                >
                  {skipping ? "Skipping…" : "Yes, skip for now"}
                </button>
              </div>
            </div>
          ) : (
            <div className="op-actions">
              {isFirstVisit && (
                <button
                  type="button"
                  className="op-btn-skip"
                  onClick={() => setShowSkipConfirm(true)}
                  disabled={saving}
                >
                  <SkipForward size={16} aria-hidden />
                  Skip for now
                </button>
              )}
              <button type="submit" className="op-submit" disabled={saving || skipping}>
                {saving ? "Saving…" : status.profileComplete ? "Save changes" : "Save & go to Dashboard"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
